import { Badge, Button, SummaryOverviewCard, type SummaryOverviewCardFields } from "randmarcomps"
import React from "react"
import { Link } from "react-router-dom"
import Path from "../../routes/Path"
import OpenPDFButton from "../util/OpenPDFButton"
import { ClipboardList, FileText, ReceiptText } from "lucide-react"
import FormatDateFromInt from "../util/DateTimeUtil"

interface IProps {
  documentNumber: string
  applicationId?: string
  document: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null

type DocumentField = {
  label: string
  value: React.ReactNode
  truncate?: boolean
  mono?: boolean
}

type DocumentKind = "purchase-order" | "requisition" | "reception" | "document"

const getDocumentKind = (documentNumber: string): DocumentKind => {
  if (documentNumber.startsWith("PO") && documentNumber.length === 9) {
    return "purchase-order"
  }
  if (documentNumber.startsWith("RQ") && documentNumber.length === 9) {
    return "requisition"
  }
  if (documentNumber.startsWith("RC") && documentNumber.length === 9) {
    return "reception"
  }
  return "document"
}

const toSingleSentence = (rawValue: string, fallback: string) => {
  const normalized = rawValue.replace(/\s+/g, " ").trim()
  if (!normalized) return fallback

  const firstSentenceMatch = normalized.match(/^[\s\S]*?[.!?](?:\s|$)/)
  const firstSentence = (firstSentenceMatch?.[0] || normalized).trim()
  const limited = firstSentence.length > 140 ? `${firstSentence.slice(0, 137).trimEnd()}...` : firstSentence

  if (!limited) return fallback
  if (/[.!?]$/.test(limited)) return limited
  return `${limited}.`
}

const DocumentCard: React.FC<IProps> = ({ documentNumber, applicationId, document: rawDocument }) => {
  const document = React.useMemo(() => {
    if (!isRecord(rawDocument)) return {}
    return rawDocument
  }, [rawDocument])

  const readString = React.useCallback((...keys: string[]) => {
    for (const key of keys) {
      const value = document[key]
      if (typeof value === "string" && value.trim() !== "") return value.trim()
    }
    return null
  }, [document])

  const readNumber = React.useCallback((...keys: string[]) => {
    for (const key of keys) {
      const value = document[key]
      if (typeof value === "number" && Number.isFinite(value)) return value
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value)
        if (!Number.isNaN(parsed)) return parsed
      }
    }
    return null
  }, [document])

  const readBoolean = React.useCallback((...keys: string[]) => {
    for (const key of keys) {
      const value = document[key]
      if (typeof value === "boolean") return value
      if (typeof value === "number") return value !== 0
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase()
        if (normalized === "true" || normalized === "yes") return true
        if (normalized === "false" || normalized === "no") return false
      }
    }
    return null
  }, [document])

  const formatDate = React.useCallback((value: number | null) => {
    if (!value || value <= 19900101) return "—"
    const formatted = FormatDateFromInt(value)
    return typeof formatted === "string" ? formatted : String(formatted)
  }, [])

  const kind = React.useMemo(() => getDocumentKind(documentNumber), [documentNumber])

  const header = React.useMemo(() => {
    switch (kind) {
      case "purchase-order":
        return { title: "Purchase Order", Icon: FileText }
      case "requisition":
        return { title: "Requisition", Icon: ClipboardList }
      case "reception":
        return { title: "Reception", Icon: ReceiptText }
      default:
        return { title: "Document", Icon: FileText }
    }
  }, [kind])

  const location = React.useMemo(() => readString("Location", "location") || "—", [readString])
  const locationName = React.useMemo(() => readString("LocationName", "locationName"), [readString])
  const vendorName = React.useMemo(
    () => readString("VendorName", "vendorName", "ManufacturerName", "manufacturerName"),
    [readString]
  )
  const requestedBy = React.useMemo(() => readString("RequestedBy", "requestedBy"), [readString])
  const reference = React.useMemo(() => readString("Reference", "reference"), [readString])

  const subTotal = React.useMemo(() => readNumber("SubTotal", "subTotal"), [readNumber])
  const total = React.useMemo(() => readNumber("Total", "total"), [readNumber])

  const purchaseOrderNumber = React.useMemo(
    () => readString("PurchaseOrderNumber", "purchaseOrderNumber"),
    [readString]
  )
  const invoiceNumber = React.useMemo(() => readString("InvoiceNumber", "invoiceNumber"), [readString])
  const invoiced = React.useMemo(() => readBoolean("Invoiced", "invoiced"), [readBoolean])

  const requisitionDate = React.useMemo(
    () => readNumber("RequisitionDate", "requisitionDate"),
    [readNumber]
  )
  const receiptDate = React.useMemo(() => readNumber("ReceiptDate", "receiptDate"), [readNumber])
  const purchaseOrderDate = React.useMemo(
    () => readNumber("PurchaseOrderDate", "purchaseOrderDate", "OrderDate", "orderDate"),
    [readNumber]
  )
  const requiredByDate = React.useMemo(() => readNumber("RequiredByDate", "requiredByDate"), [readNumber])
  const expectedArrivalDate = React.useMemo(
    () => readNumber("ExpectedArrivalDate", "expectedArrivalDate"),
    [readNumber]
  )

  const formattedSubTotal = React.useMemo(() => {
    const value = subTotal
    if (value === null) return "—"
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(value)
  }, [subTotal])

  const formattedTotal = React.useMemo(() => {
    if (total === null) return "—"
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(total)
  }, [total])

  const formattedReceiptDate = React.useMemo(() => formatDate(receiptDate), [formatDate, receiptDate])
  const formattedRequisitionDate = React.useMemo(() => formatDate(requisitionDate), [formatDate, requisitionDate])
  const formattedPurchaseOrderDate = React.useMemo(() => formatDate(purchaseOrderDate), [formatDate, purchaseOrderDate])
  const formattedRequiredByDate = React.useMemo(() => formatDate(requiredByDate), [formatDate, requiredByDate])
  const formattedExpectedArrivalDate = React.useMemo(
    () => formatDate(expectedArrivalDate),
    [expectedArrivalDate, formatDate]
  )

  const description = React.useMemo(() => {
    const loc = locationName ? `${location} — ${locationName}` : location
    const fallback = "Document summary."

    switch (kind) {
      case "reception":
        return toSingleSentence(`Reception for ${loc} on ${formattedReceiptDate}`, fallback)
      case "requisition":
        return toSingleSentence(
          `${requestedBy || "Requisition"} requested items for ${loc} on ${formattedRequisitionDate}`,
          fallback
        )
      case "purchase-order":
        return toSingleSentence(
          `${vendorName || "Purchase Order"} is scheduled for ${loc} on ${formattedPurchaseOrderDate}`,
          fallback
        )
      default:
        return toSingleSentence(`Document for ${loc}`, fallback)
    }
  }, [
    formattedPurchaseOrderDate,
    formattedReceiptDate,
    formattedRequisitionDate,
    kind,
    location,
    locationName,
    requestedBy,
    vendorName,
  ])

  const fields = React.useMemo<DocumentField[]>(() => {
    if (kind === "reception") {
      return [
        { label: "Purchase Order", value: purchaseOrderNumber || "—", mono: true },
        { label: "Receipt Date", value: formattedReceiptDate },
        { label: "Location", value: locationName ? `${location} — ${locationName}` : location, truncate: true },
        { label: "Subtotal", value: formattedSubTotal },
        { label: "Total", value: formattedTotal },
        {
          label: "Invoiced",
          value: (
            <Badge variant={invoiced ? "success" : "secondary"} size="sm" className="w-fit">
              {invoiced ? "Yes" : "No"}
            </Badge>
          ),
        },
        { label: "Invoice", value: invoiceNumber || "—", mono: true },
      ]
    }

    if (kind === "requisition") {
      return [
        { label: "Requested By", value: requestedBy || "—", truncate: true },
        { label: "Requisition Date", value: formattedRequisitionDate },
        { label: "Required By", value: formattedRequiredByDate },
        { label: "Location", value: locationName ? `${location} — ${locationName}` : location, truncate: true },
        { label: "Subtotal", value: formattedSubTotal },
        { label: "Reference", value: reference || "—", truncate: true },
      ]
    }

    if (kind === "purchase-order") {
      return [
        { label: "Vendor", value: vendorName || "—", truncate: true },
        { label: "Order Date", value: formattedPurchaseOrderDate },
        { label: "Expected Arrival", value: formattedExpectedArrivalDate },
        { label: "Location", value: locationName ? `${location} — ${locationName}` : location, truncate: true },
        { label: "Subtotal", value: formattedSubTotal },
        { label: "Total", value: formattedTotal },
      ]
    }

    const defaultDate =
      formattedReceiptDate !== "—"
        ? formattedReceiptDate
        : formattedRequisitionDate !== "—"
          ? formattedRequisitionDate
          : formattedPurchaseOrderDate !== "—"
            ? formattedPurchaseOrderDate
            : "—"

    const comments = readString("Comments", "comments")

    return [
      { label: "Date", value: defaultDate },
      { label: "Vendor", value: vendorName || "—", truncate: true },
      { label: "Location", value: locationName ? `${location} — ${locationName}` : location, truncate: true },
      { label: "Subtotal", value: formattedSubTotal },
      { label: "Total", value: formattedTotal },
      { label: "Comments", value: comments || "—", truncate: true },
    ]
  }, [
    formattedExpectedArrivalDate,
    formattedPurchaseOrderDate,
    formattedReceiptDate,
    formattedRequiredByDate,
    formattedRequisitionDate,
    formattedSubTotal,
    formattedTotal,
    invoiceNumber,
    invoiced,
    kind,
    location,
    locationName,
    purchaseOrderNumber,
    readString,
    reference,
    requestedBy,
    vendorName,
  ])

  const summaryFields = React.useMemo<SummaryOverviewCardFields>(() => {
    return fields.map((field) => {
      if (typeof field.value !== "string") {
        return {
          label: field.label,
          value: field.value,
        }
      }

      const value = field.value || "—"
      const className = [
        field.mono ? "font-mono" : "",
        field.truncate ? "block truncate" : "",
      ]
        .filter(Boolean)
        .join(" ")

      const title = field.truncate && value !== "—" ? value : undefined
      const renderedValue = className ? (
        <span className={className} title={title}>
          {value}
        </span>
      ) : (
        value
      )

      return {
        label: field.label,
        value: renderedValue,
      }
    }) as SummaryOverviewCardFields
  }, [fields])

  return (
    <SummaryOverviewCard
      icon={header.Icon}
      contentType={header.title}
      identifier={documentNumber}
      description={description}
      fields={summaryFields}
      highlightedHeader={kind === "purchase-order"}
      className="group rounded-2xl border border-border shadow-[var(--shadow-card)] transition-shadow hover:border-primary/30 hover:shadow-[var(--shadow-card-hover)]"
      actions={[
        <OpenPDFButton key="pdf" documentNumber={documentNumber} stealth />,
        <Button key="view" asChild variant="default" className="min-h-[44px]">
          <Link to={Path.document(applicationId, documentNumber)}>View</Link>
        </Button>,
      ]}
    />
  )
}

export default DocumentCard
